import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { animationTransition } from '@shared/utils/animationTransition';

import React from 'react';
import { useTransition, animated } from 'react-spring';
import type { AuxPhoto } from '../../AuxFrame.types';
import { addRendition } from '@shared/utils/photoRendition';

export const AuxFramePhotoSlider: React.FC<{ photo: AuxPhoto }> = ({ photo }) => {
  // Keyed on the image, not the photo record: two sections can point at distinct Photo
  // records holding the same image, and re-keying on `id` would cross-fade a picture
  // against an identical copy. Mirrored opacities compose to `1 - a + a²`, which dips to
  // 0.75 mid-transition and lets the page background show through the aux pane — the photo
  // appears to flicker in place. Same URL now means the layer is simply retained.
  const transition = useTransition(photo, item => item.url, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { tension: 220, friction: 120 },
  });

  return (
    <>
      {transition.map(({ item, props, key }) => (
        <animated.div
          key={key}
          style={{
            position: 'absolute',
            pointerEvents: 'none',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            height: '100vh',
            backgroundImage: backgroundImageForUrl(addRendition({ url: item.url, renditionSize: 'xlarge' })),
            backgroundSize: 'cover',
            // From `item`, never the live `photo` prop: react-spring freezes a leaving layer's
            // item, so reading the prop made the outgoing image jerk to the incoming photo's
            // focal point at the start of every fade.
            backgroundPosition: `${item.layout.alignX} ${item.layout.alignY}`,
            backgroundRepeat: 'no-repeat',
            // Unconditional: only a retained layer's focal point can change (a leaving layer's
            // item is frozen, an entering one has no previous value to ease from), and that is
            // exactly the case that wants easing.
            transition: animationTransition('background-position'),
            ...props,
          }}
        />
      ))}
    </>
  );
};
